import React, { useEffect, useRef, useState } from 'react';

type Language = 'zh' | 'en';

type WorkflowStep = {
  number: string;
  eyebrow: string;
  title: string;
  description: string;
  image: string;
  caption: string;
};

type LandingCopy = {
  hero: {
    eyebrow: string;
    title: React.ReactNode;
    description: string;
    workflowAction: string;
    monthlyOutput: string;
    monthlyOutputUnit: React.ReactNode;
    monthlyGrowth: string;
    industryEfficiency: string;
    industryEfficiencyUnit: React.ReactNode;
  };
  eyebrow: string;
  title: React.ReactNode;
  description: string;
  workflowLabel: string;
  closingEyebrow: string;
  closingTitle: React.ReactNode;
  closingDescription: string;
  startCreatingAction: string;
  workflow: WorkflowStep[];
};

const COPY: Record<Language, LandingCopy> = {
  zh: {
    hero: {
      eyebrow: 'AI SHORT DRAMA STUDIO',
      title: <>让每个故事<br /><em>被看见</em></>,
      description: '从一段灵感到一部短剧。VibeReels 用 AI 重塑剧本、角色、分镜与成片，让创作者专注故事，让制作从此轻盈。',
      workflowAction: '看看它如何工作',
      monthlyOutput: '月产能',
      monthlyOutputUnit: <>部+<br />精品剧</>,
      monthlyGrowth: '月增长',
      industryEfficiency: '行业第一人效 · 1人1周',
      industryEfficiencyUnit: <>分钟<br />精品剧</>,
    },
    eyebrow: 'ONE FLOW, FULL STORY',
    title: <>一个工作台，<br />走完创作全程。</>,
    description: '没有割裂的工具链，也没有反复搬运素材。你的剧本、角色与镜头，在同一条创作脉络里持续生长。',
    workflowLabel: 'VibeReels 创作流程',
    closingEyebrow: 'YOUR STORY STARTS HERE',
    closingTitle: <>下一个让人追更的故事，<br />从这里开始。</>,
    closingDescription: '不必等待完整团队，也不必被复杂流程打断。把第一个镜头交给 VibeReels，让灵感更快抵达观众。',
    startCreatingAction: '免费开始创作',
    workflow: [
      {
        number: '01',
        eyebrow: 'SCRIPT ANALYSIS',
        title: 'AI 先读懂故事，建立创作坐标',
        description: '系统从剧本中提炼类型、一句话故事、核心设定与剧情阶段，把长篇文本整理成团队可以直接理解和执行的创作结构。',
        image: '/homepage-workflow-01.png',
        caption: '剧本信息 · 世界观与剧情阶段',
      },
      {
        number: '02',
        eyebrow: 'ASSET DESIGN',
        title: '角色与状态图，沉淀为可复用资产',
        description: '根据人物设定生成角色原型、服装和不同剧情状态。视觉资产与角色说明集中管理，为后续镜头保持一致性。',
        image: '/homepage-workflow-02.png',
        caption: '资产设定 · 角色与状态管理',
      },
      {
        number: '03',
        eyebrow: 'VIDEO CREATION',
        title: '分镜、提示词与素材，在同一画面完成',
        description: '逐镜管理画面、人物、场景和生成提示词，并直接预览生成结果。创作者可以继续编辑、匹配资源或重新生成。',
        image: '/homepage-workflow-03.png',
        caption: '视频创作 · 分镜与生成结果',
      },
      {
        number: '04',
        eyebrow: 'FINAL OUTPUT',
        title: '从工作台走向真正可观看的成片',
        description: '最终画面以竖屏短剧规格输出，人物、场景和叙事动作延续前面的设定，让故事从文字一路抵达观众。',
        image: '/homepage-workflow-04.png',
        caption: '最终成片 · 竖屏短剧输出',
      },
    ],
  },
  en: {
    hero: {
      eyebrow: 'AI SHORT DRAMA STUDIO',
      title: <>Let every story<br /><em>be seen</em></>,
      description: 'From an idea to a short drama. VibeReels uses AI to connect scripts, characters, shots, and final episodes so creators can stay focused on the story.',
      workflowAction: 'See how it works',
      monthlyOutput: 'MONTHLY OUTPUT',
      monthlyOutputUnit: <>PREMIUM<br />EPISODES</>,
      monthlyGrowth: 'MONTHLY GROWTH',
      industryEfficiency: 'INDUSTRY-LEADING OUTPUT',
      industryEfficiencyUnit: <>MINUTES<br />PER EPISODE</>,
    },
    eyebrow: 'ONE FLOW, FULL STORY',
    title: <>One workspace,<br />from script to screen.</>,
    description: 'No fragmented toolchain or repeated asset handoffs. Your script, characters, and shots grow together in one connected creative workflow.',
    workflowLabel: 'VibeReels creative workflow',
    closingEyebrow: 'YOUR STORY STARTS HERE',
    closingTitle: <>Your next binge-worthy story<br />starts here.</>,
    closingDescription: 'You do not need a full production team or a complicated process. Hand VibeReels the first shot and bring your idea to viewers sooner.',
    startCreatingAction: 'Start creating for free',
    workflow: [
      {
        number: '01',
        eyebrow: 'SCRIPT ANALYSIS',
        title: 'AI understands the story before production begins',
        description: 'Turn a long script into an actionable creative brief with genre, logline, world setting, and story beats that the whole team can use.',
        image: '/homepage-workflow-01.png',
        caption: 'Script context · World and story beats',
      },
      {
        number: '02',
        eyebrow: 'ASSET DESIGN',
        title: 'Characters become reusable visual assets',
        description: 'Create character concepts, wardrobe, and scene-specific states from the original brief, then manage them centrally for visual continuity.',
        image: '/homepage-workflow-02.png',
        caption: 'Asset setup · Character and state management',
      },
      {
        number: '03',
        eyebrow: 'VIDEO CREATION',
        title: 'Shots, prompts, and assets in one view',
        description: 'Manage every shot, character, scene, and generation prompt together while previewing results and refining production without context switching.',
        image: '/homepage-workflow-03.png',
        caption: 'Video creation · Shots and generated results',
      },
      {
        number: '04',
        eyebrow: 'FINAL OUTPUT',
        title: 'From the workspace to a watchable episode',
        description: 'Produce vertical short-form drama that carries forward the characters, settings, and story actions established throughout the workflow.',
        image: '/homepage-workflow-04.png',
        caption: 'Final output · Vertical drama delivery',
      },
    ],
  },
};

interface HomepageStoryProps {
  language: Language;
  children: React.ReactNode;
  onStartCreating: () => void;
  landingRef: React.RefObject<HTMLDivElement>;
  closingLogin: React.ReactNode;
}

export function HomepageStory({ language, children, onStartCreating, landingRef, closingLogin }: HomepageStoryProps) {
  const copy = COPY[language];
  const [activeStep, setActiveStep] = useState(0);
  const stepRefs = useRef<Array<HTMLElement | null>>([]);

  useEffect(() => {
    if (!('IntersectionObserver' in window)) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const mostVisible = entries
          .filter(entry => entry.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];
        if (!mostVisible) return;
        setActiveStep(Number((mostVisible.target as HTMLElement).dataset.step));
      },
      { rootMargin: '-25% 0px -35% 0px', threshold: [0.2, 0.45, 0.7] },
    );

    stepRefs.current.forEach(step => step && observer.observe(step));
    return () => observer.disconnect();
  }, []);

  const scrollToWorkflow = () => {
    document.getElementById('workflow')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <div ref={landingRef} className="hp-landing">
      <section className="hp-new-hero" aria-label={copy.hero.eyebrow}>
        <div className="hp-new-hero-inner">
          <div className="hp-new-eyebrow"><span /> {copy.hero.eyebrow}</div>
          <h2 className="hp-new-hero-title">{copy.hero.title}</h2>
          <p className="hp-new-hero-copy">{copy.hero.description}</p>
          <button className="hp-new-hero-action" type="button" onClick={scrollToWorkflow}>
            {copy.hero.workflowAction}<span aria-hidden="true">↓</span>
          </button>

          <div className="hp-new-orbit" aria-hidden="true">
            <div className="hp-new-orbit-ring hp-new-ring-one" />
            <div className="hp-new-orbit-ring hp-new-ring-two" />
            <div className="hp-new-film-card hp-new-metric-card hp-new-card-a">
              <span>{copy.hero.monthlyOutput}</span>
              <div><strong>100</strong><b>{copy.hero.monthlyOutputUnit}</b></div>
            </div>
            <div className="hp-new-film-card hp-new-metric-card hp-new-card-b">
              <span>{copy.hero.monthlyGrowth}</span>
              <div><strong>100</strong><b className="hp-new-metric-percent">%</b></div>
            </div>
            <div className="hp-new-film-card hp-new-metric-card hp-new-metric-wide hp-new-card-c">
              <span>{copy.hero.industryEfficiency}</span>
              <div><strong>100</strong><b>{copy.hero.industryEfficiencyUnit}</b></div>
            </div>
            <div className="hp-new-play-core"><span>▶</span></div>
          </div>

        </div>
      </section>

      <section className="hp-landing-intro" id="workflow" aria-label={copy.workflowLabel}>
        <div className="hp-landing-shell">
          <div className="hp-landing-kicker">{copy.eyebrow}</div>
          <div className="hp-landing-intro-grid">
            <h2>{copy.title}</h2>
            <p>{copy.description}</p>
          </div>
        </div>
      </section>

      <section className="hp-workflow hp-landing-shell" aria-label={copy.workflowLabel}>
        <div className="hp-workflow-copy">
          {copy.workflow.map((step, index) => (
            <article
              key={step.number}
              className={`hp-workflow-step ${activeStep === index ? 'is-active' : ''}`}
              data-step={index}
              ref={node => { stepRefs.current[index] = node; }}
            >
              <span className="hp-workflow-number">{step.number}</span>
              <span className="hp-landing-kicker">{step.eyebrow}</span>
              <h3>{step.title}</h3>
              <p>{step.description}</p>
              <div className="hp-workflow-mobile-shot">
                <img src={step.image} alt="" />
              </div>
            </article>
          ))}
        </div>

        <div className="hp-workflow-stage" aria-live="polite">
          <div className="hp-workflow-window">
            <div className="hp-workflow-window-bar">
              <span className="hp-workflow-window-dots" aria-hidden="true"><i /><i /><i /></span>
              <strong>VibeReels / {copy.workflowLabel}</strong>
              <small>{activeStep + 1} / {copy.workflow.length}</small>
            </div>
            <div className="hp-workflow-image">
              {copy.workflow.map((step, index) => (
                <img
                  key={step.image}
                  className={activeStep === index ? 'is-visible' : ''}
                  src={step.image}
                  alt={activeStep === index ? step.caption : ''}
                />
              ))}
            </div>
            <div className="hp-workflow-caption">
              <span>{copy.workflow[activeStep].number}</span>
              <strong>{copy.workflow[activeStep].caption}</strong>
              <i />
            </div>
          </div>
          <div className="hp-workflow-dots" aria-hidden="true">
            {copy.workflow.map((step, index) => (
              <i key={step.number} className={activeStep === index ? 'is-active' : ''} />
            ))}
          </div>
        </div>
      </section>

      {children}

      <section className="hp-landing-closing">
        <div className="hp-landing-closing-glow" />
        <div className="hp-landing-shell hp-landing-closing-content">
          <div className="hp-landing-kicker">{copy.closingEyebrow}</div>
          <h2>{copy.closingTitle}</h2>
          <p>{copy.closingDescription}</p>
          {closingLogin || (
            <button className="hp-landing-showcase-button" type="button" onClick={onStartCreating}>
              {copy.startCreatingAction}<span aria-hidden="true">→</span>
            </button>
          )}
        </div>
      </section>
    </div>
  );
}
